import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, Grid } from '@mui/material';
import {
  LocalizationProvider,
  TimePicker,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  ROUTER_PATHS,
  TIPO_PARAMETRO_CHOICE,
  TipoParametroChoiceEnum,
  gridSizeMdLg6,
  parametroFormSchema,
} from '@/shared';
import {
  CreateParametroParamsForm,
  useCreateParametro,
  useUpdateParametro,
} from '@/shared/actions/erp';
import {
  CustomAutocompleteArrString,
  CustomTextArea,
  CustomTextField,
  SampleCheckbox,
  SampleDatePicker,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { Parametro } from '@/shared/interfaces';
import { TEXTAREA_TYPES, TEXTFIELD_TYPES } from '../../constants';
import { useSystemParamsStore } from '@/store/erp';

export interface SaveParametroProps {
  title: string;
  parametro?: Parametro;
}

type SaveFormData = CreateParametroParamsForm & {
  es_socket?: boolean
};

const SaveParametro: React.FC<SaveParametroProps> = ({ title, parametro }) => {
  const navigate = useNavigate();
  const returnUrlParametrosPage =
    ROUTER_PATHS.administration.systemParametersNav;

  const emitParametroSistema = useSystemParamsStore((s) => s.emitParametroSistema);

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(parametroFormSchema) as any,
    defaultValues: {
      estado: true,
      tipo: TipoParametroChoiceEnum.TEXTO,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const watchedType = form.watch('tipo');

  ///* mutations
  const createParametroMutation = useCreateParametro({
    navigate,
    returnUrl: returnUrlParametrosPage,
  });
  const updateParametroMutation = useUpdateParametro({
    navigate,
    returnUrl: returnUrlParametrosPage,
  });

  ///* handlers
  const onSave = async (data: SaveFormData) => {
    if (!isValid) return;

    if (data.es_socket) {

      emitParametroSistema(data)
    }
    ///* upd
    if (parametro?.id_parametro) {
      updateParametroMutation.mutate({ id: parametro.id_parametro, data });
      return;
    }

    ///* create
    createParametroMutation.mutate(data);
  };

  ///* effects
  useEffect(() => {
    if (!parametro?.id_parametro) return;
    reset(parametro);
  }, [parametro, reset]);

  return (
    <SingleFormBoxScene
      titlePage={title}
      onCancel={() => navigate(returnUrlParametrosPage)}
      onSave={handleSubmit(onSave, () => { })}
    >
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        size={gridSizeMdLg6}
      />

      <CustomAutocompleteArrString
        label="Tipo de Parámetro"
        name="tipo"
        control={form.control}
        defaultValue={form.getValues('tipo')}
        options={TIPO_PARAMETRO_CHOICE}
        isLoadingData={false}
        error={errors.tipo}
        helperText={errors.tipo?.message}
        size={gridSizeMdLg6}
        disableClearable
      />
      <>
        {TEXTFIELD_TYPES.includes(watchedType) ? (
          <CustomTextField
            label="Valor"
            name="valor"
            control={form.control}
            defaultValue={form.getValues().valor}
            error={errors.valor}
            helperText={errors.valor?.message}
            type={
              watchedType === TipoParametroChoiceEnum.NUMERICO
                ? 'number'
                : 'text'
            }
            disabled={!watchedType}
          />
        ) : watchedType === TipoParametroChoiceEnum.BOOLEANO ? (
          <CustomAutocompleteArrString
            label="Valor"
            name="valor"
            control={form.control}
            defaultValue={form.getValues('valor')}
            options={['TRUE', 'FALSE']}
            isLoadingData={false}
            error={errors.valor}
            helperText={errors.valor?.message}
            disableClearable
          />
        ) : watchedType === TipoParametroChoiceEnum.FECHA ? (
          <SampleDatePicker
            label="Valor"
            name="valor"
            control={form.control}
            defaultValue={dayjs().format('YYYY-MM-DD')}
            error={errors.valor}
            helperText={errors.valor?.message}
          />
        ) : watchedType === TipoParametroChoiceEnum.HORA ? (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Controller
                control={form.control}
                name="valor"
                render={({ field }) => {
                  return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        {...field}
                        value={field.value || null}
                        label="Hora de finalización técnico"
                        slotProps={{
                          textField: {
                            error: !!errors.valor,
                            required: true,
                            helperText: errors.valor?.message,
                          },
                        }}
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                      />
                    </LocalizationProvider>
                  );
                }}
              />
            </FormControl>
          </Grid>
        ) : TEXTAREA_TYPES.includes(watchedType) ? (
          <>
            <CustomTextArea
              label="Valor"
              name="valor"
              type='none'
              textTransform='none'
              control={form.control}
              defaultValue={form.getValues().valor}
              error={errors.valor}
              helperText={errors.valor?.message}
              rows={6}
            />
          </>
        ) : (
          <></>
        )}
      </>

      <CustomTextArea
        label="Descripcion"
        name="descripcion"
        control={form.control}
        defaultValue={form.getValues().descripcion}
        error={errors.descripcion}
        helperText={errors.descripcion?.message}
      />

      <CustomTextField
        label="Slug"
        name="slug"
        control={form.control}
        defaultValue={form.getValues().slug}
        error={errors.slug}
        helperText={errors.slug?.message}
        disabled={!!parametro?.id_parametro}
      />

      <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        defaultValue={form.getValues().codigo}
        error={errors.codigo}
        helperText={errors.codigo?.message}
        disabled={!!parametro?.id_parametro}
      />

      <SampleCheckbox
        label="Enviar por Socket"
        name="es_socket"
        control={form.control}
        defaultValue={!!form.getValues().es_socket}
        size={gridSizeMdLg6}
        sxChecbox={{ pb: 2 }}
      />

    </SingleFormBoxScene>
  );
};

export default SaveParametro;
